/** Mappe la réponse GET /capitalflow/homepage vers la forme attendue par InvelpsCapitalApp (bundles). */

const INDEX_META = {
  JP: { reg: "Asia", ctry: "JP", name: "Nikkei 225" },
  KR: { reg: "Asia", ctry: "KR", name: "KOSPI" },
  GB: { reg: "Europe", ctry: "GB", name: "FTSE 100" },
  DE: { reg: "Europe", ctry: "DE", name: "DAX" },
  CN: { reg: "Asia", ctry: "CN", name: "SSE Composite" },
  AU: { reg: "Asia", ctry: "AU", name: "ASX 200" },
  IN: { reg: "Asia", ctry: "IN", name: "Nifty 50" },
  AE: { reg: "Middle East", ctry: "AE", name: "Tadawul" },
  AR: { reg: "Americas", ctry: "AR", name: "Merval" },
  FR: { reg: "Europe", ctry: "FR", name: "CAC 40" },
  HK: { reg: "Asia", ctry: "HK", name: "FTSE China A50" },
  US: { reg: "Americas", ctry: "US", name: "S&P 500" },
  BR: { reg: "Americas", ctry: "BR", name: "Bovespa" },
  CL: { reg: "Americas", ctry: "CL", name: "IPSA" },
  IT: { reg: "Europe", ctry: "IT", name: "FTSE MIB" },
};

function sectorShortName(sector) {
  if (!sector) return "";
  return sector
    .replace("Information Technology", "Info. Tech.")
    .replace("Communication Services", "Comm. Services")
    .replace("Consumer Staples", "Cons. Staples")
    .replace("Consumer Discretionary", "Consumer Disc.")
    .replace("Health Care", "Health");
}

export function fDateFr(d) {
  if (!d) return "—";
  const s = String(d);
  const part = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  const [y, m] = part.split("-");
  if (!y || !m) return s;
  const mois = [
    "",
    "Janv.",
    "Fév.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juil.",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ];
  return `${mois[parseInt(m, 10)]} ${y}`;
}

function buildStratCurve(topRow) {
  if (!topRow) return [];
  const labels = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
  const bpm =
    topRow.b_monthly_proxy != null
      ? Number(topRow.b_monthly_proxy)
      : Number(topRow.b_annual_return || 0) / 12;
  let cumS = 0;
  return labels.map((m, i) => {
    const mv = topRow[`m${i + 1}`];
    if (mv != null && Number.isFinite(Number(mv))) cumS += Number(mv);
    return { m, s: cumS, b: bpm * (i + 1) };
  });
}

/**
 * @param {object} api — JSON HomepageDataView
 * @returns {object} même forme que getStaticHomeBundle()
 */
export function buildHomeBundleFromApi(api) {
  if (!api || api.error || !api.cache) return null;

  const cache = { ...api.cache };
  const regionTooltip = (region) =>
    cache[`tooltip_region_${String(region || "").replace(/ /g, "")}`] || "";
  const sectorTooltip = (sector) =>
    cache[`tooltip_sector_${String(sector || "").replace(/ /g, "").replace(/\//g, "")}`] || "";
  const indexTooltip = (symbol) => cache[`tooltip_index_${symbol}`] || "";

  const RFLOWS = (api.region_flows || []).map((r) => ({
    region: r.region,
    cnf_pct: r.cnf_pct,
    flow_state: r.flow_state,
    flow_sens_score: r.flow_sens_score,
    wyckoff: r.wyckoff,
  }));

  const SECTORS_D = (api.sector_rotation || []).map((r) => ({
    sector: r.sector,
    name: sectorShortName(r.sector),
    cnf_pct: r.cnf_pct,
    pnf: r.pnf != null ? r.pnf : r.nfp_pct,
    flow_state: r.flow_state,
    ret: r.ret,
    size: r.size,
  }));

  const SECTORS_D_VIEW = SECTORS_D.map((d) => ({
    ...d,
    name: sectorShortName(d.sector),
    pnf: d.pnf,
    ret: d.ret,
  }));

  const INDICES_D = (api.indices || [])
    .map((r) => {
      const sym = r.symbol;
      const meta = INDEX_META[sym] || { reg: "", ctry: sym || "", name: r.index_name || sym };
      return {
        sym,
        reg: meta.reg,
        ctry: meta.ctry,
        name: r.index_name || meta.name,
        sig: r.signal,
        s50: r.signal_50,
        s200: r.signal_200,
        vs50: r.vs50,
        vs200: r.vs200,
        ret: r.return_m1,
      };
    })
    .sort((a, b) => {
      if (a.ret == null && b.ret == null) return 0;
      if (a.ret == null) return 1;
      if (b.ret == null) return -1;
      return b.ret - a.ret;
    });

  const LEADERS_D = (api.leadership || []).map((r, i) => ({
    rank: i + 1,
    scope: r.scope,
    name: r.name,
    leader_symbol: r.leader_symbol,
    ret_m1: r.ret_m1,
    flow_state: r.flow_state,
    persistence: r.persistence ?? 0,
    market_iso2: r.market_iso2,
    sector: r.sector,
    mkt_class: r.mkt_class,
    style: r.style,
  }));

  const OPPS_D = (api.opportunities || []).map((o) => ({
    sym: o.symbol,
    name: o.company_name || "",
    sig: o.signal,
    is: o.iscore,
    fs: o.fund_score,
    tr: o.trend_score,
    mos: o.mos_pct,
    roic_vs_wacc: o.roic_vs_wacc,
    global_score: o.global_score,
    these: [o.market_label, o.moat_label, o.valuation_label].filter(Boolean).join(" · "),
  }));

  const STRATS_D = (api.strategies || []).map((r) => ({
    s: r.strategy,
    a: "",
    fy: r.fy_monthly,
    cagr_5y: r.cagr_5y,
    sharpe_1y: r.sharpe_1y,
    mdd_1y: r.mdd_1y,
  }));

  const STRAT_CURVE = buildStratCurve((api.strategies || [])[0]);

  const PULSE = (api.market_pulse || []).map((r) => ({
    region: r.region,
    v: r.cnf_pct,
    b: r.b_monthly,
    p: r.pnf,
  }));

  const bc = Number(cache.buy_count) || 0;
  const wc = Number(cache.watch_count) || 0;
  const ac = Number(cache.avoid_count) || 0;
  const BUY_RATE = bc + wc + ac > 0 ? ((bc / (bc + wc + ac)) * 100).toFixed(1) : "0.0";
  const REGIME_TONE =
    cache.market_regime === "DISTRIBUTION" ? "r" : cache.market_regime === "ACCUMULATION" ? "g" : "w";

  return {
    CACHE: cache,
    RFLOWS,
    SECTORS_D,
    SECTORS_D_VIEW,
    INDICES_D,
    LEADERS_D,
    OPPS_D,
    STRATS_D,
    STRAT_CURVE,
    PULSE,
    regionTooltip,
    sectorTooltip,
    indexTooltip,
    fDateFr,
    BUY_RATE,
    REGIME_TONE,
  };
}
